// Google Ads attribution for the signup funnel. A gclid reaches this app one of two ways:
// the marketing site (www.medendo.com) captures it into a cookie scoped to .medendo.com,
// which is readable here on workspace.medendo.com, or a campaign links straight to the
// signup page and the gclid arrives as a URL parameter. Whichever it is, the value is
// forwarded to the Stripe Checkout Session so a later conversion can be matched back to
// the ad click that started it.
const GCLID_PARAM = 'gclid'
const GCLID_COOKIE = 'medendo_gclid'

// A gclid is opaque. Google documents no format and can change its encoding without notice,
// so nothing here may assume a charset — URL-safe base64 today, padded or otherwise tomorrow.
// Rejecting on shape would silently lose attribution for genuine paid signups, which is the
// exact failure this feature exists to remove. What counts as a usable value is decided once,
// at the backend boundary, which does not trust this code anyway.
//
// The one bound left is Stripe's documented ceiling for a metadata value, which is where the
// gclid ends up: past it the value cannot be stored at all, so there is nothing to be gained
// by carrying it any further. Deliberately the same number the backend enforces.
const GCLID_MAX_LENGTH = 500

function readCookie(name: string): string | undefined {
    for (const entry of document.cookie.split('; ')) {
        const separator = entry.indexOf('=')
        if (separator > 0 && entry.slice(0, separator) === name) {
            // decodeURIComponent throws URIError on malformed percent-encoding, and this runs
            // on the way into checkout: a corrupt cookie has to cost the campaign its
            // attribution, never cost the customer their signup.
            try {
                return decodeURIComponent(entry.slice(separator + 1))
            } catch {
                return undefined
            }
        }
    }

    return undefined
}

// Trimmed on the way out of storage, where surrounding whitespace is an artefact of how the
// cookie was written rather than part of the identifier — the marketing site's snippet is not
// ours, and a stray newline there should not cost a real click its attribution. The backend
// does not trim what it receives, and should not: there the value is an opaque identifier
// arriving over an API, not a string being read out of a jar.
function usableGclid(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim()

    return trimmed && trimmed.length <= GCLID_MAX_LENGTH ? trimmed : undefined
}

// The URL decides whenever it carries a gclid at all, usable or not, because that parameter is
// the click that is happening now. Falling through to the cookie when the URL's own value turns
// out to be unusable would attach an earlier visit's click to this checkout, and a conversion
// reported against the wrong ad click is worse than one not reported at all: it corrupts the
// campaign data this feature exists to produce, rather than merely failing to add to it.
export function readGclid(): string | undefined {
    const fromUrl = new URLSearchParams(window.location.search).get(GCLID_PARAM)
    if (fromUrl !== null) {
        return usableGclid(fromUrl)
    }

    return usableGclid(readCookie(GCLID_COOKIE))
}
